/**
 * <nw-header-drawer> wraps the mobile <details class="nw-header__mobile-toggle">
 * menu. A native <details>/<summary> only knows how to open/close itself via
 * its own <summary>, so this adds the rest of what a side-drawer is expected
 * to do: close on the dedicated X button, close on tapping the dimmed
 * backdrop (the ::before painted on the <details> itself), and close on
 * Escape.
 *
 * This used to be a global function that ran once on 'DOMContentLoaded' /
 * 'shopify:section:load', found the drawer via document.querySelector, and
 * marked it done with a dataset ready-flag so it wouldn't bind twice. A
 * custom element doesn't need any of that bookkeeping: connectedCallback
 * runs for this exact instance as soon as the browser connects it, so it
 * can't end up silently unbound - the same reasoning behind
 * <device-finder-form> (assets/device-finder.js) and <nw-header-search-form>
 * (assets/nw-header-search.js).
 */
class NwHeaderDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('details');
    if (!this.toggle) return;

    this.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const { toggle } = this;
    if (!toggle || !toggle.open) return;

    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-nw-drawer-close]')) {
      toggle.open = false;
      return;
    }

    const inPanel = target.closest('.nw-header__mobile-panel');
    const inSummary = target.closest('summary');
    if (!inPanel && !inSummary) toggle.open = false;
  };

  /** @param {KeyboardEvent} event */
  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.toggle) this.toggle.open = false;
  };
}

if (!customElements.get('nw-header-drawer')) {
  customElements.define('nw-header-drawer', NwHeaderDrawer);
}
